import React from "react";
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from "react-hook-form";
import { Form, Spinner } from "react-bootstrap";
import { useAuth } from "../../../../auth/context/AuthProvider";
import useIndexAgent from "../../../../services/agent/hooks/useIndexAgent";

interface AgentSelectComponentProps {
    control: Control<any>;
    errors: FieldErrors<{ filter: { agent_id?: string } }>;
    setValue: UseFormSetValue<any>;
}

// OTA and Agent are mutually exclusive — a booking is sourced from one or the
// other, never both, so picking one here clears and disables the OTA filter.
const AgentSelectComponent: React.FC<AgentSelectComponentProps> = ({ control, errors, setValue }) => {
    const { user } = useAuth();
    const { data: agents, isFetching, error } = useIndexAgent({
        filters: { all: 1, property_id: user?.active_property_id },
    });

    const otaCode = useWatch({ control, name: "filter.ota_code" });
    const disabled = !!otaCode;

    return (
        <div>
            <Form.Label className="me-2">Filter Agent:</Form.Label>

            {isFetching ? (
                <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading agents...</span>
                </div>
            ) : error ? (
                <div className="text-danger">Failed to load agents</div>
            ) : (
                <Controller
                    name="filter.agent_id"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Form.Select
                                {...field}
                                value={field.value ?? ""}
                                isInvalid={!!errors.filter?.agent_id}
                                disabled={disabled}
                                onChange={(e) => {
                                    field.onChange(e);
                                    if (e.target.value) {
                                        setValue("filter.ota_code", "");
                                    }
                                }}
                            >
                                <option value="">{disabled ? "Disabled (OTA selected)" : "All Agents"}</option>
                                {agents?.data.map((agent) => (
                                    <option key={agent.id} value={agent.id}>
                                        {agent.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {errors.filter?.agent_id && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.filter.agent_id.message as string}
                                </Form.Control.Feedback>
                            )}
                        </>
                    )}
                />
            )}
        </div>
    );
};

export default AgentSelectComponent;
